import { SortOption } from "@/ui/SortDropdown/types";
import {
  CONTRACT_STATUS,
  GRANT_APPLICATION_DELIVERABLE_PAYMENT_STATUS,
  GRANT_APPLICATION_DELIVERABLE_STATUS,
  PROJECT_SECTION,
} from "@thrivecoin/adapter/dist/types";
import { GeneralFieldType } from "../types";
import { ItemConfig } from "@/components/Activity/FundingDeliverableRow/types";
import { KYCVerificationStatus } from "@/components/Profile/KYCVerification/types";
import { GRANT_APPLICATION_WALLET_VERIFICATION_STATUS } from "@/components/Activity/GrantApplicationWalletVerificationStatus/types";
import { IconName } from "@/ui/Icon/type";
import { MilestonePaymentTextConfig } from "@/components/modals/PaymentDetailsModal/types";
import { MilestoneRowPaymentTextConfig } from "@/components/Activity/DeliverableRowBottomText/types";

export const DEFAULT_IMAGE_URL = "https://avatars.githubusercontent.com/u/7300207?v=4";

export const LOCAL_STORAGE_ACTIVITY_TYPE_KEY = "activity-type";

export const REFERRAL_CODE_KEY = "referral-code";

export const URL_WITHOUT_PROTOCOL_REGEX = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/.*)?$/;

export const IS_STAGING = window.origin.includes("staging");
export const IS_LOCALLY = window.location.hostname === "localhost";
export const IS_PROD = !IS_STAGING && !IS_LOCALLY;

export const PROFILE_URL = import.meta.env.PROD
  ? !IS_STAGING
    ? "https://contributors.thriveprotocol.com/"
    : "https://staging.contributors.thriveprotocol.com/"
  : "http://localhost:5555";

export const DEFAULT_LINKS = [
  { text: "Home", value: "/", external: false },
  // { text: "Leaderboard", value: "/leaderboard", external: false },
  // { text: "Contributions", value: "https://app.thrive.xyz/", external: false },
  // { text: "Leaderboard", value: "#", external: false },
  // { text: "Staking", value: "#", external: false },
];

export const PROTECTED_LINKS = IS_PROD
  ? [
      {
        text: "Activity",
        value: "/activity",
        external: false,
      },
      // { text: "Referrals", value: "/referrals", external: false },
    ]
  : [
      { text: "Reviews", value: "/reviews", external: false },
      { text: "Activity", value: "/activity", external: false },
      // { text: "Referrals", value: "/referrals", external: false },
    ];

export const X_URL = "https://x.com/thriveprotocol";
export const DISCORD_URL = "https://discord.com/invite/thriveprotocol";

export const dashboardSortOptions: SortOption[] = [
  { label: "Submission Deadline", value: "submission_deadline", icon: "calendar" },
  { label: "Funding Pool", value: "grant_pool", icon: "coins" },
  { label: "# of Applicants", value: "num_of_applicants", icon: "users2" },
  { label: "Ecosystem", value: "ecosystem", icon: "globe" },
];

export const reviewsSortOptions: SortOption[] = [
  { label: "Ecosystem", value: "ecosystem", icon: "globe" },
  { label: "Reward amount", value: "reward_amount", icon: "coins" },
  { label: "Status", value: "status", icon: "chart-line" },
  // { label: "Grants", value: "grants", icon: "medal" },
];

export const primaryButtonTextByStep: string[] = ["Continue", "Continue", "Submit Application"];
export const secondaryButtonTextByStep: string[] = ["Cancel", "Cancel", "Edit"];

export const PREVIOUS_PATHNAME_STORAGE_KEY = "previousPathname";
export const CURRENT_PATHNAME_STORAGE_KEY = "currentPathname";

export const allowedProviders = ["github", "linkedin", "twitter", "farcaster"];

export const PROJECT_BUILDER_STEPS = [
  { title: "Project Overview" },
  { title: "Team & Background" },
  { title: "Traction & Validation" },
  { title: "Value Proposition" },
  { title: "Funding & Use of Funds" },
  { title: "Growth Strategy" },
  { title: "Final Checks" },
];

export const statusOptions = [
  { value: "stealth", label: "Stealth" },
  { value: "under_development", label: "Under Development" },
  { value: "on_test", label: "On Testnet" },
  { value: "live_on_mainnet", label: "Live on Mainnet" },
];

export const technologyOptions = [
  { value: "EVM", label: "EVM" },
  { value: "Hashgraph", label: "Hashgraph" },
  { value: "Solana", label: "Solana" },
  { value: "CosmWasm", label: "CosmWasm" },
  { value: "Other", label: "Other" },
];

export const verticalOptions = [
  { value: "Gaming", label: "Gaming" },
  { value: "GambleFi", label: "GambleFi" },
  { value: "Lending/Borrowing", label: "Lending/Borrowing" },
  { value: "Restaking", label: "Restaking" },
  { value: "DeFi", label: "DeFi" },
  { value: "RWA", label: "RWA" },
  { value: "SocialFi", label: "SocialFi" },
  { value: "DePin", label: "DePin" },
  { value: "Infra", label: "Infra" },
  { value: "Launchpad", label: "Launchpad" },
  { value: "Other", label: "Other" },
];

export const PROJECT_OVERVIEW_FIELDS = [
  "logo",
  "name",
  "description",
  "category_ids",
  "github_url",
  "url",
  "status",
  "telegram_handle",
  "technology",
  "vertical",
];

export const PROJECT_TEAM_BACKGROUND_FIELDS = ["team_members", "team_size_fte"];

export const PROJECT_VALUE_PROPOSITION_FIELDS = [
  "unique_value_proposition",
  "long_term_ecosystem_value",
  "aligned_thrive_ecosystems",
  "chains_infra_tools",
  "collaborate_with_thrive",
];

export const PROJECT_FUNDING_FIELDS = ["funding_raised_to_date", "notable_investors_backers", "goals_next_3_6_months"];

export const PROJECT_GROWTH_FIELDS = ["go_to_market_strategy", "success_next_year", "supporting_materials"];

export const PROJECT_FINAL_FIELDS = [
  "video_walkthrough",
  "prior_thrive_application",
  "heard_about_thrive",
  "referrer_name",
  "revenue_model",
];

export const PROJECT_INFO_FIELDS: GeneralFieldType = {
  logo: {
    key: "logo",
    label: "Upload project logo or image",
    responseLabel: "Project logo or image",
    type: "image",
  },
  name: {
    key: "name",
    label: "Project Name",
    type: "short_text",
    placeholder: "Enter project name",
    section: PROJECT_SECTION.OVERVIEW,
  },
  description: {
    key: "description",
    label: "Project Description",
    type: "long_text",
    placeholder: "Provide a brief overview of your project...",
    section: PROJECT_SECTION.OVERVIEW,
  },
  category_ids: {
    key: "category_ids",
    label: "Select project categories",
    responseLabel: "Project Categories",
    type: "multi_select",
    placeholder: "Select a project category",
    section: PROJECT_SECTION.OVERVIEW,
  },
  github_url: {
    key: "github_url",
    label: "GitHub Repository URL",
    type: "short_text",
    placeholder: "https://github.com/ThriveCoin",
    section: PROJECT_SECTION.OVERVIEW,
  },
  url: {
    key: "url",
    label: "Project Website URL",
    type: "short_text",
    placeholder: "https://",
    section: PROJECT_SECTION.OVERVIEW,
  },
  status: {
    key: "status",
    label: "Project Status",
    type: "single_select",
    placeholder: "Select project status",
    options: statusOptions,
    section: PROJECT_SECTION.OVERVIEW,
  },
  telegram_handle: {
    key: "telegram_handle",
    label: "Telegram Handle",
    type: "short_text",
    placeholder: "Enter your Telegram handle",
    section: PROJECT_SECTION.OVERVIEW,
  },
  technology: {
    key: "technology",
    label: "Technology",
    type: "multi_select",
    placeholder: "Select a technology",
    options: technologyOptions,
    section: PROJECT_SECTION.OVERVIEW,
  },
  vertical: {
    key: "vertical",
    label: "Vertical",
    type: "multi_select",
    placeholder: "Select a vertical",
    options: verticalOptions,
    section: PROJECT_SECTION.OVERVIEW,
  },
  user_email: {
    key: "user_email",
    label: "User Email",
    type: "short_text",
    section: PROJECT_SECTION.OVERVIEW,
  },
  user_address: {
    key: "user_address",
    label: "User Address",
    type: "short_text",
    section: PROJECT_SECTION.OVERVIEW,
  },
  team_members: {
    key: "team_members",
    label: "List your team members, roles, and LinkedIn or X accounts",
    type: "long_text",
    placeholder: "E.g. Alice (CEO) – linkedin.com/in/alice, Bob (CTO) – x.com/bob",
    section: PROJECT_SECTION.TEAM,
  },
  team_size_fte: {
    key: "team_size_fte",
    label: "Number of FTEs (full-time equivalents) on the team",
    type: "short_text",
    placeholder: "E.g. 5",
    section: PROJECT_SECTION.TEAM,
  },
  traction_proof_points: {
    key: "traction_proof_points",
    label: "What traction or proof points can you share?",
    type: "long_text",
    placeholder: "E.g. 50K+ users, $10M TVL, partnerships with major protocols, featured in CoinDesk...",
    section: PROJECT_SECTION.TRACTION,
  },
  public_data_description: {
    key: "public_data_description",
    label: "Share any public dashboards or metrics",
    type: "long_text",
    placeholder: "E.g. dune.xyz/project, tokenterminal.com/xyz...",
    section: PROJECT_SECTION.TRACTION,
  },
  key_milestones_achieved: {
    key: "key_milestones_achieved",
    label: "What key milestones have you already achieved?",
    type: "long_text",
    placeholder: "E.g. Testnet live, $1M raised, 100 devs...",
    section: PROJECT_SECTION.TRACTION,
  },
  impact_pmfit_measurement: {
    key: "impact_pmfit_measurement",
    label: "How do you currently measure impact or product-market fit?",
    type: "long_text",
    placeholder: "E.g. Retention, revenue, user feedback...",
    section: PROJECT_SECTION.TRACTION,
  },
  unique_value_proposition: {
    key: "unique_value_proposition",
    label: "What is your project's unique value proposition?",
    type: "long_text",
    placeholder: "E.g. Simplifies onboarding for web3 users...",
    section: PROJECT_SECTION.VALUE,
  },
  long_term_ecosystem_value: {
    key: "long_term_ecosystem_value",
    label: "How does your project create long-term value for ecosystems?",
    type: "long_text",
    placeholder: "E.g. Helps scale dev adoption and community engagement...",
    section: PROJECT_SECTION.VALUE,
  },
  aligned_thrive_ecosystems: {
    key: "aligned_thrive_ecosystems",
    label: "Which Thrive partner ecosystems are most aligned with your work, and why?",
    type: "long_text",
    placeholder: "E.g. Optimism for infra focus, Arbitrum for dev tooling...",
    section: PROJECT_SECTION.VALUE,
  },
  chains_infra_tools: {
    key: "chains_infra_tools",
    label: "Which chains, infra providers, or tools are you already using or plan to use?",
    type: "long_text",
    placeholder: "E.g. Ethereum, IPFS, WalletConnect, The Graph...",
    section: PROJECT_SECTION.VALUE,
  },
  collaborate_with_thrive: {
    key: "collaborate_with_thrive",
    label: "Would you collaborate with Thrive teams?",
    type: "long_text",
    placeholder: "E.g. Yes — open to sharing infra, co-building, or design support...",
    section: PROJECT_SECTION.VALUE,
  },
  funding_raised_to_date: {
    key: "funding_raised_to_date",
    label: "How much funding have you raised to date (if any)?",
    type: "long_text",
    placeholder: "E.g. $500K seed round, $2M Series A, or No funding raised yet...",
    section: PROJECT_SECTION.FUNDS,
  },
  notable_investors_backers: {
    key: "notable_investors_backers",
    label: "List any notable investors, grant programs, or backers (optional)",
    type: "long_text",
    placeholder: "E.g. Ethereum Foundation, a16z crypto, Coinbase Ventures, Protocol Labs...",
    section: PROJECT_SECTION.FUNDS,
  },
  goals_next_3_6_months: {
    key: "goals_next_3_6_months",
    label: "What are your top 3 goals for the next 3–6 months, and how will you measure success?",
    type: "long_text",
    placeholder:
      "E.g. 1) Launch mainnet (measure: successful deployment), 2) Reach 10K users (measure: active wallets), 3) Integrate 5 partners (measure: live integrations)...",
    section: PROJECT_SECTION.FUNDS,
  },
  go_to_market_strategy: {
    key: "go_to_market_strategy",
    label: "What's your go-to-market or user acquisition strategy?",
    type: "long_text",
    placeholder: "E.g. Community campaigns, KOL partnerships, grants...",
    section: PROJECT_SECTION.GROWTH,
  },
  success_next_year: {
    key: "success_next_year",
    label: "What does success look like for your project in the next year?",
    type: "long_text",
    placeholder: "E.g. 50K users, mainnet launch, top 3 in category...",
    section: PROJECT_SECTION.GROWTH,
  },
  supporting_materials: {
    key: "supporting_materials",
    label: "Any supporting materials to share?",
    type: "long_text",
    placeholder: "E.g. Pitch deck, demo link, GitHub, Notion doc...",
    section: PROJECT_SECTION.GROWTH,
  },
  video_walkthrough: {
    key: "video_walkthrough",
    label: "Upload video/walkthrough showcasing your project",
    responseLabel: "Video/walkthrough showcasing your project",
    type: "video",
    section: PROJECT_SECTION.ADDITIONAL,
  },
  prior_thrive_application: {
    key: "prior_thrive_application",
    label: "Have you or a teammate applied to Thrive before? If yes, when?",
    type: "long_text",
    placeholder: "E.g. Yes, applied in Q2 2023 but wasn't selected, or No, first time applying...",
    section: PROJECT_SECTION.ADDITIONAL,
  },
  heard_about_thrive: {
    key: "heard_about_thrive",
    label: "How did you hear about Thrive Protocol?",
    type: "long_text",
    placeholder: "E.g. Twitter/X, Discord community, referral from friend, conference presentation...",
    section: PROJECT_SECTION.ADDITIONAL,
  },
  referrer_name: {
    key: "referrer_name",
    label: "Name of Referrer",
    type: "short_text",
    placeholder: "Enter the name of the Referrer",
    section: PROJECT_SECTION.ADDITIONAL,
  },
  revenue_model: {
    key: "revenue_model",
    label: "Please outline your project's revenue model",
    type: "long_text",
    placeholder: "E.g. 2% protocol fee, SaaS subscription, token utility for premium features...",
    section: PROJECT_SECTION.ADDITIONAL,
  },
};

export const DELIVERABLE_STATUS_CONFIG: Record<GRANT_APPLICATION_DELIVERABLE_STATUS, ItemConfig> = {
  pending: {
    iconName: null,
    iconContainerClassName: "border border-[#2B2D31] bg-[#2B2D31]",
    iconClassName: "",
    actionLabel: "Submit",
  },
  in_progress: {
    iconName: "dot",
    iconContainerClassName: "border border-primary bg-[#0c0d0d]",
    iconClassName: "text-primary",
    actionLabel: "In Review",
  },
  rejected: {
    iconName: "dot",
    iconContainerClassName: "border border-primary bg-[#0c0d0d]",
    iconClassName: "text-primary",
    actionLabel: "Re-Submit",
  },
  completed: {
    iconName: "check",
    iconContainerClassName: "bg-primary",
    iconClassName: "",
    actionLabel: "Completed",
  },
};

export const KYC_STATUS_CONFIG: Record<KYCVerificationStatus, ItemConfig> = {
  not_started: {
    iconName: null,
    iconContainerClassName: "border border-[#2B2D31] bg-[#2B2D31]",
    iconClassName: "",
    actionLabel: "Start Verification",
  },
  created: {
    iconName: null,
    iconContainerClassName: "border border-[#2B2D31] bg-[#2B2D31]",
    iconClassName: "",
    actionLabel: "Continue Verification",
  },
  pending: {
    iconName: null,
    iconContainerClassName: "border border-[#2B2D31] bg-[#2B2D31]",
    iconClassName: "",
    actionLabel: "In Review",
  },
  approved: {
    iconName: "check",
    iconContainerClassName: "bg-primary",
    iconClassName: "",
    actionLabel: "Approved",
  },
  rejected_temporary: {
    iconName: "x",
    iconContainerClassName: "bg-primary",
    iconClassName: "",
    actionLabel: "Re-submit Verification",
  },
  rejected_final: {
    iconName: "x",
    iconContainerClassName: "bg-primary",
    iconClassName: "",
    actionLabel: "Permanently Rejected",
  },
};

export const CONTRACT_STATUS_CONFIG: Record<CONTRACT_STATUS, ItemConfig> = {
  initialized: {
    iconName: null,
    iconContainerClassName: "border border-[#2B2D31] bg-[#2B2D31]",
    iconClassName: "",
    actionLabel: "Sign Agreement",
  },
  draft: {
    iconName: null,
    iconContainerClassName: "border border-[#2B2D31] bg-[#2B2D31]",
    iconClassName: "",
    actionLabel: "Sign Agreement",
  },
  pending: {
    iconName: "dot",
    iconContainerClassName: "border border-primary bg-[#0c0d0d]",
    iconClassName: "text-primary",
    actionLabel: "Sign Agreement",
  },
  sent: {
    iconName: "dot",
    iconContainerClassName: "border border-primary bg-[#0c0d0d]",
    iconClassName: "text-primary",
    actionLabel: "Sign Agreement",
  },
  signed: {
    iconName: "check",
    iconContainerClassName: "bg-primary",
    iconClassName: "",
    actionLabel: "Signed",
  },
  completed: {
    iconName: "check",
    iconContainerClassName: "bg-primary",
    iconClassName: "",
    actionLabel: "Signed",
  },
  cancelled: {
    iconName: "x",
    iconContainerClassName: "bg-primary",
    iconClassName: "",
    actionLabel: "Cancelled",
  },
  rejected: {
    iconName: "x",
    iconContainerClassName: "bg-primary",
    iconClassName: "",
    actionLabel: "Rejected",
  },
};

export const KYC_DELIVERABLES = [
  {
    id: 1,
    title: "Government-issued ID",
    description: "(Passport, National ID card, or Driver's License — front and back)",
  },
  {
    id: 2,
    title: "Selfie with ID",
    description: "(To confirm identity match and liveness)",
  },
  {
    id: 3,
    title: "Proof of Address with ID",
    description: "(Utility bill, bank statement, or official document dated within last 3 months)",
  },
  {
    id: 4,
    title: "Completed KYC form",
    description: "(Personal info: full name, date of birth, nationality, address, etc.)",
  },
];

export const FUNDING_AGREEMENT_DELIVERABLES = [
  {
    id: 1,
    title: "Review funding program terms",
    description: "(Carefully read through funding amount, milestones, and payment schedule)",
  },
  {
    id: 2,
    title: "Digital signature",
    description: "(Sign the agreement electronically using your verified identity)",
  },
  {
    id: 3,
    title: "Acknowledgment of terms",
    description: "(Accept milestone requirements, reporting obligations, and compliance terms)",
  },
];

export const MILESTONE_COLLAPSIBLE_COMMON_PROPS = {
  className: "milestone-collapsible p-4",
  showFullSeparator: true,
};

export const WALLET_VERIFICATION_STATUS_CONFIG: Record<GRANT_APPLICATION_WALLET_VERIFICATION_STATUS, ItemConfig> = {
  not_verified: {
    iconName: null,
    iconContainerClassName: "border border-[#2B2D31] bg-[#2B2D31]",
    iconClassName: "",
    actionLabel: "Verify Wallet",
  },
  verified: {
    iconName: "check",
    iconContainerClassName: "bg-primary",
    iconClassName: "",
    actionLabel: "Verified",
  },
};

export const WALLET_PROVIDER_ICON_MAP: Record<string, IconName> = {
  unknown: "ethereum",
  metamask: "metamask",
  coinbase: "coinbase",
  phantom: "phantom",
  hedera: "hedera",
};

export const milestonePaymentTextConfig: Record<
  GRANT_APPLICATION_DELIVERABLE_PAYMENT_STATUS,
  MilestonePaymentTextConfig
> = {
  waiting: { text: "Payment In Progress", textClassName: "text-warning" },
  not_eligible: { text: "Not Eligible", textClassName: "text-error-light" },
  paid: {
    text: "Successfully Received",
    textClassName: "text-medium-green",
  },
};

export const milestoneRowPaymentTextConfig: Record<
  GRANT_APPLICATION_DELIVERABLE_PAYMENT_STATUS,
  MilestoneRowPaymentTextConfig
> = {
  waiting: {
    text: "Payment in progress",
    textClassName: "text-warning",
  },
  paid: {
    text: "Payment completed",
    textClassName: "text-medium-green",
  },
  not_eligible: {
    text: "Payment not eligible",
    textClassName: "text-error-light",
  },
};

export const GUARDIAN_APPLICATION_STEPS = [
  { title: "Overview" },
  { title: "Personal Information" },
  { title: "Social Links" },
  { title: "Application Details" },
];

export const GUARDIAN_APPLICATION_TITLE = "Apply to become a reviewer";

export const SUBMISSION_LOCK_STORAGE_KEY = "THC_LOCK_EXPIRES_AT";
